// ======================================
// Admin Settings Page Module
// ======================================

import { icons } from '../../components.js';
import { requireAuth, getUser, logout } from '../../lib/auth.js';
import { usersAPI } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderAdminSettings() {
  if (!requireAuth(['admin'])) {
    return '<div class="loading">Redirecting...</div>';
  }

  const user = getUser();

  const content = `
          <!-- Tabs -->
          <div class="settings-tabs">
            <button class="settings-tab active" data-tab="users">Team Members</button>
            <button class="settings-tab" data-tab="business">Business Settings</button>
          </div>
          
          <!-- Users Tab -->
          <div class="settings-panel" id="users-panel">
            <div class="admin-card">
              <div class="admin-card-header">
                <h2>Team Members</h2>
                <button class="btn btn-primary btn-sm" id="add-user-btn">
                  ${icons.plus || '+'} Add User
                </button>
              </div>
              <div class="admin-card-body" id="users-container">
                <div class="loading-placeholder">Loading users...</div>
              </div>
            </div>
          </div>
          
          <!-- Business Settings Tab -->
          <div class="settings-panel" id="business-panel" style="display: none;">
            <div class="admin-card">
              <div class="admin-card-header">
                <h2>Business Information</h2>
              </div>
              <div class="admin-card-body">
                <form id="business-form" class="settings-form">
                  <div class="form-grid">
                    <div class="form-group">
                      <label class="form-label">Business Name</label>
                      <input type="text" name="business_name" class="form-input" 
                             value="NammaVeetuInteriors" readonly>
                    </div>
                    <div class="form-group">
                      <label class="form-label">Contact Email</label>
                      <input type="email" name="contact_email" class="form-input" 
                             value="hello@nammaveetuinteriors.com">
                    </div>
                    <div class="form-group">
                      <label class="form-label">Contact Phone</label>
                      <input type="tel" name="contact_phone" class="form-input" 
                             value="+91 98765 43210">
                    </div>
                    <div class="form-group col-span-2">
                      <label class="form-label">Address</label>
                      <textarea name="address" class="form-textarea" rows="2">123 Design Street, Anna Nagar, Chennai - 600040</textarea>
                    </div>
                  </div>
                  <div class="form-actions">
                    <button type="submit" class="btn btn-primary">Save Changes</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
    
    <!-- Add User Modal -->
    <div class="modal-overlay" id="user-modal" style="display:none;">
      <div class="modal-dialog" style="max-width:500px;">
        <div class="modal-header">
          <h2 class="modal-title" id="user-modal-title">Add Team Member</h2>
          <button class="modal-close" id="user-modal-close">&times;</button>
        </div>
        <div class="modal-body" id="user-modal-body"></div>
      </div>
    </div>
  `;

  return renderAdminLayout('users', 'Settings & Users', content);
}

export async function initAdminSettings() {
  initAdminLayout();

  // Tab switching
  const tabs = document.querySelectorAll('.settings-tab');
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      document.querySelectorAll('.settings-panel').forEach(panel => {
        panel.style.display = 'none';
      });
      document.getElementById(`${tab.dataset.tab}-panel`).style.display = 'block';
    });
  });

  // Load users
  await loadUsers();

  // Add user button
  const addBtn = document.getElementById('add-user-btn');
  if (addBtn) {
    addBtn.addEventListener('click', () => showAddUserModal());
  }

  // Modal handlers
  const modal = document.getElementById('user-modal');
  const modalClose = document.getElementById('user-modal-close');

  if (modalClose) modalClose.addEventListener('click', () => closeUserModal());
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeUserModal();
    });
  }
}

async function loadUsers() {
  const container = document.getElementById('users-container');
  if (!container) return;

  try {
    const response = await usersAPI.list();

    if (response.success && response.data?.length > 0) {
      container.innerHTML = `
        <table class="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            ${response.data.map(user => `
              <tr>
                <td>
                  <div class="user-info">
                    <div class="user-avatar">${user.name.charAt(0).toUpperCase()}</div>
                    <span>${user.name}</span>
                  </div>
                </td>
                <td>${user.email}</td>
                <td><span class="badge badge-${user.role}">${user.role}</span></td>
                <td>
                  <span class="status-badge status-${user.is_active ? 'active' : 'inactive'}">
                    ${user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-icon" onclick="editUser(${user.id})" title="Edit">✏️</button>
                    <button class="btn-icon" onclick="toggleUserStatus(${user.id}, ${!user.is_active})" 
                            title="${user.is_active ? 'Deactivate' : 'Activate'}">
                      ${user.is_active ? '🚫' : '✓'}
                    </button>
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `;
    } else {
      container.innerHTML = '<p class="empty-state">No team members found</p>';
    }
  } catch (error) {
    console.error('Failed to load users:', error);
    container.innerHTML = '<p class="error-state">Failed to load users</p>';
  }
}

function showAddUserModal() {
  const modal = document.getElementById('user-modal');
  const modalBody = document.getElementById('user-modal-body');
  const modalTitle = document.getElementById('user-modal-title');

  if (!modal || !modalBody) return;

  if (!modal || !modalBody) return;

  modalTitle.textContent = 'Add Team Member';
  modal.style.display = 'flex';

  modalBody.innerHTML = `
    <form id="add-user-form">
      <div class="form-group">
        <label class="form-label">Full Name *</label>
        <input type="text" name="name" class="form-input" required>
      </div>
      <div class="form-group">
        <label class="form-label">Email *</label>
        <input type="email" name="email" class="form-input" required>
      </div>
      <div class="form-group">
        <label class="form-label">Phone</label>
        <input type="tel" name="phone" class="form-input">
      </div>
      <div class="form-group">
        <label class="form-label">Password *</label>
        <input type="password" name="password" class="form-input" required minlength="6">
      </div>
      <div class="form-group">
        <label class="form-label">Role *</label>
        <select name="role" class="form-select" required>
          <option value="designer">Designer</option>
          <option value="manager">Manager</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      <div class="modal-actions">
        <button type="button" class="btn btn-outline" onclick="closeUserModal()">Cancel</button>
        <button type="submit" class="btn btn-primary">Add Member</button>
      </div>
    </form>
  `;

  document.getElementById('add-user-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const form = e.target;

    try {
      await usersAPI.create({
        name: form.name.value,
        email: form.email.value,
        phone: form.phone.value,
        password: form.password.value,
        role: form.role.value
      });

      closeUserModal();
      loadUsers();
    } catch (error) {
      alert(error.message || 'Failed to create user');
    }
  });
}

// Global functions
window.editUser = async function (id) {
  const modal = document.getElementById('user-modal');
  const modalBody = document.getElementById('user-modal-body');
  const modalTitle = document.getElementById('user-modal-title');

  if (!modal || !modalBody) return;

  if (!modal || !modalBody) return;

  modalTitle.textContent = 'Edit User';
  modal.style.display = 'flex';

  try {
    const response = await usersAPI.get(id);
    if (response.success && response.data) {
      const user = response.data;

      modalBody.innerHTML = `
        <form id="edit-user-form">
          <div class="form-group">
            <label class="form-label">Full Name *</label>
            <input type="text" name="name" class="form-input" value="${user.name}" required>
          </div>
          <div class="form-group">
            <label class="form-label">Phone</label>
            <input type="tel" name="phone" class="form-input" value="${user.phone || ''}">
          </div>
          <div class="form-group">
            <label class="form-label">Role *</label>
            <select name="role" class="form-select" required>
              <option value="customer" ${user.role === 'customer' ? 'selected' : ''}>Customer</option>
              <option value="designer" ${user.role === 'designer' ? 'selected' : ''}>Designer</option>
              <option value="manager" ${user.role === 'manager' ? 'selected' : ''}>Manager</option>
              <option value="admin" ${user.role === 'admin' ? 'selected' : ''}>Admin</option>
            </select>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" onclick="closeUserModal()">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Changes</button>
          </div>
        </form>
      `;

      document.getElementById('edit-user-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target;

        try {
          await usersAPI.update(id, {
            name: form.name.value,
            phone: form.phone.value,
            role: form.role.value
          });

          closeUserModal();
          loadUsers();
        } catch (error) {
          alert(error.message || 'Failed to update user');
        }
      });
    }
  } catch (error) {
    modalBody.innerHTML = '<p class="error-state">Failed to load user</p>';
  }
};

window.toggleUserStatus = async function (id, activate) {
  if (!confirm(`Are you sure you want to ${activate ? 'activate' : 'deactivate'} this user?`)) {
    return;
  }

  try {
    await usersAPI.update(id, { is_active: activate ? 1 : 0 });
    loadUsers();
  } catch (error) {
    alert('Failed to update user status');
  }
};

window.closeUserModal = function () {
  const modal = document.getElementById('user-modal');
  if (modal) modal.style.display = 'none';
};
